import React, { ReactElement } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { clientSprintRoute, type ProductPageTab, productTabPath } from '@app/constants/url';
import { useOrganizationId } from '@app/utils/hooks';
import ProductDetail from '../products/productDetail';
import SprintProductContainer from './sprintProduct';
import ClientProducts from './clientProducts';

const TABS: ProductPageTab[] = [
  'settings',
  'team',
  'sprints',
  'incidents',
  'costs',
  'monitoring',
  'quality',
  'productivity',
  'security-audit',
];

/** The Sprints tab's old address, still in mailed links and bookmarks. */
const ReportsRedirect = (): ReactElement => {
  const { productId = '' } = useParams<{ productId: string }>();
  const organizationId = useOrganizationId() ?? '';
  return <Navigate to={productTabPath(organizationId, productId, 'sprints', true)} replace />;
};

/** Monitoring was Health and Costs was Spend: an old link lands on the tab under its new name, arrow state and all. */
const RenamedTabRedirect = ({ to, view }: { to: ProductPageTab; view?: string }): ReactElement => {
  const { productId = '' } = useParams<{ productId: string }>();
  const organizationId = useOrganizationId() ?? '';
  const { state } = useLocation();
  const path = productTabPath(organizationId, productId, to, true);
  return <Navigate to={view ? `${path}?view=${view}` : path} replace state={state} />;
};

/** Dashboard and Insights were tabs of their own before the dashboard became the product's front page. */
const DashboardRedirect = (): ReactElement => {
  const { productId = '' } = useParams<{ productId: string }>();
  const organizationId = useOrganizationId() ?? '';
  return <Navigate to={productTabPath(organizationId, productId, 'dashboard', true)} replace />;
};

/**
 * Nested routes under `…/client-reporting/*` so `useParams()` exposes `productId` and `sprintId`.
 *
 * A client lands on their products, opens one on the same product page we read, and a single sprint
 * report keeps its own page.
 */
const ClientReportingRouter = (): ReactElement => (
  <Routes>
    <Route path={clientSprintRoute} element={<SprintProductContainer />} />
    <Route path="products/:productId" element={<ProductDetail asClient />} />
    {TABS.map(tab => (
      <Route key={tab} path={`products/:productId/${tab}`} element={<ProductDetail tab={tab} asClient />} />
    ))}
    <Route path="products/:productId/reports" element={<ReportsRedirect />} />
    <Route path="products/:productId/health" element={<RenamedTabRedirect to="monitoring" />} />
    <Route path="products/:productId/spend" element={<RenamedTabRedirect to="costs" />} />
    <Route path="products/:productId/budget" element={<RenamedTabRedirect to="costs" view="budgeted" />} />
    <Route path="products/:productId/dashboard" element={<DashboardRedirect />} />
    <Route path="products/:productId/insights" element={<DashboardRedirect />} />
    <Route path="*" element={<ClientProducts />} />
  </Routes>
);

export default ClientReportingRouter;
